import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, Sparkles, Milestone, Rocket } from 'lucide-react';
import './StudentTrophyRoom.css'; // Importing our standalone layout styles
import { fetchStudentProfileBadges } from '../api/studentApi';

const StudentTrophyRoom = () => {
    const [showcase, setShowcase] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadAchievements = async () => {
            setLoading(true);
            const achievements = await fetchStudentProfileBadges();
            console.log(achievements.data.profileShowcase);
            setShowcase(achievements.data.profileShowcase);
            console.log('Data: ', showcase);
            setLoading(false);
        };
        loadAchievements();
    }, []);

    if (loading) {
        return (
            <div className="trophy-room-loading">
                <Rocket className="loading-spinner-icon" size={32} />
                <p className="loading-text">Loading Star Charts & Badges...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="trophy-room-error-alert">
                ⚠️ {error}
            </div>
        );
    }

    // Configurations dictionary matching Lucide components straight to tier maps
    const categoryMeta = {
        COMPLETION: { title: "Mission Milestones", IconComponent: Milestone, classKey: "completion" },
        MASTERY: { title: "Topic Exploration", IconComponent: Trophy, classKey: "mastery" },
        FEAT: { title: "Cosmic Achievements", IconComponent: Sparkles, classKey: "feat" },
        STREAK: { title: "Learning Streaks", IconComponent: Rocket, classKey: "streak" }
    };

    const renderBadgeGrid = (categoryKey, badgeList) => {
        if (!badgeList || badgeList.length === 0) return null;
        const meta = categoryMeta[categoryKey] || { title: categoryKey, IconComponent: Trophy, classKey: "generic" };
        const CategoryIcon = meta.IconComponent;

        // Calculate how many items are unlocked in this track
        const unlockedCount = badgeList.filter(b => b.isUnlocked).length;

        return (
            <div className="badge-category-section">
                {/* Category Header Bar */}
                <div className="category-header-bar">
                    <div className="category-title-group">
                        <CategoryIcon className={`category-header-icon icon-${meta.classKey}`} size={22} />
                        <h3 className="category-section-title">{meta.title}</h3>
                    </div>
                    <span className="category-fraction-pill">
                        {unlockedCount} / {badgeList.length}
                    </span>
                </div>

                {/* Badge Render Cards Grid */}
                <div className="badge-cards-grid">
                    {badgeList.map((badge) => (
                        <div
                            key={badge.id}
                            className={`badge-card-container ${badge.isUnlocked ? 'unlocked' : 'locked'}`}
                        >
                            {/* Multi-earn Count Floating Bubble */}
                            {badge.timesEarned > 1 && (
                                <span className="multi-earn-badge-bubble">
                                    {badge.timesEarned}
                                </span>
                            )}

                            {/* Space Badge Artwork Content Frame */}
                            <div className="badge-image-wrapper">
                                <img
                                    src={badge.imageUrl}
                                    alt={badge.name}
                                    className="badge-image-asset"
                                    loading="lazy"
                                    onError={(e) => { e.target.src = 'https://placehold.co/64x64/f1f5f9/94a3b8?text=Locked'; }}
                                />
                            </div>

                            {/* Text Info Stack */}
                            <div className="badge-text-stack">
                                <h4 className="badge-item-name">{badge.name}</h4>
                                <p className="badge-item-description">{badge.description}</p>
                            </div>

                            {/* Status Label Pill UI footer alignment */}
                            <div className="badge-status-pill-footer">
                                <span className={`status-pill ${badge.isUnlocked ? 'pill-unlocked' : 'pill-locked'}`}>
                                    {badge.isUnlocked ? 'Unlocked' : 'Locked'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="trophy-room-master-wrapper">

            {/* Render the core categories using explicitly destructured layout keys */}
            {showcase && (
                <div className="trophy-room-categories-container">
                    {renderBadgeGrid("COMPLETION", showcase.COMPLETION)}
                    {renderBadgeGrid("MASTERY", showcase.MASTERY)}
                    {renderBadgeGrid("FEAT", showcase.FEAT)}
                    {renderBadgeGrid("STREAK", showcase.STREAK)}
                </div>
            )}
        </div>
    );
};

export default StudentTrophyRoom;